import { createContext } from 'react';
import type { WorkflowFilters } from '../hooks/workflow';
import type { BatchWorkflowListItem, WorkflowDashboardData } from '../types/workflow';
import type { UserStatusPermissions } from '../types/workflow/statusRole';

/**
 * WorkflowContextValue defines all values and functions provided by the workflow context.
 * This interface mirrors UseWorkflowDashboardReturn for type safety and documentation.
 */
export interface WorkflowContextValue {
  // Filters
  /** Current filter state */
  filters: WorkflowFilters;
  /** Set a single filter value */
  setFilter: <K extends keyof WorkflowFilters>(key: K, value: WorkflowFilters[K]) => void;
  /** Set multiple filter values at once */
  setFilters: (filters: Partial<WorkflowFilters>) => void;
  /** Reset all filters to defaults */
  resetFilters: () => void;
  /** Whether any non-default filters are active */
  hasActiveFilters: boolean;
  /** Count of active filters */
  activeFilterCount: number;

  // Data
  /** Array of batch workflow items */
  batches: BatchWorkflowListItem[];
  /** Total count of items matching current filters */
  totalCount: number;
  /** Total number of pages */
  totalPages: number;
  /** Workflow dashboard statistics */
  stats: WorkflowDashboardData | null;

  // Loading/Error states
  /** Whether any data is loading */
  loading: boolean;
  /** Whether batches are loading */
  batchesLoading: boolean;
  /** Whether stats are loading */
  statsLoading: boolean;
  /** Error message if any fetch failed */
  error: string | null;

  // Selection
  /** Set of selected batch IDs */
  selectedIds: Set<number>;
  /** Count of selected items */
  selectedCount: number;
  /** Check if a batch is selected */
  isSelected: (id: number) => boolean;
  /** Toggle selection of a single batch */
  toggleSelection: (id: number) => void;
  /** Select all currently visible batches */
  selectAll: () => void;
  /** Clear all selections */
  clearSelection: () => void;

  // Actions
  /** Refetch both batches and stats */
  refetch: () => void;

  // Permissions
  /** Current user's workflow status permissions (null while loading or on error) */
  userPermissions: UserStatusPermissions | null;
  /** Whether permissions are currently being fetched */
  permissionsLoading: boolean;
  /** Refetch the user's permissions */
  refetchPermissions: () => Promise<void>;
}

/**
 * React Context for workflow dashboard state.
 * Initialized to null - consumers must be wrapped in WorkflowProvider.
 */
export const WorkflowContext = createContext<WorkflowContextValue | null>(null);
